/**
 * ImageUrlScannerDialog - Dialog for scanning and migrating external images to CDN
 *
 * Step-based flow:
 * 1. MIGRATE: Scan HTML, show images list, migrate selected
 * 2. PREVIEW: Show simple diff (old → new URLs), download/apply
 *
 * Used by OnboardingCustomHtmlSection for CustomScreen editor.
 */

import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  LinearProgress,
  Checkbox,
  Chip,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Radio,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import { SDialog } from '@modules/ui-components';
import type { OnboardingUploadContext } from '@modules/firebase-core/services/OnboardingMediaUploadService';
import {
  scanHtmlForImageUrls,
  filterExternalUrls,
  replaceUrlsInHtml,
  downloadMappingJson,
  downloadUpdatedScript,
  matchLocalFilesToPaths,
  type UrlMapping,
} from '../../../../utils/imageUrlScanner';
import {
  ImageMigrationService,
  CorsBlockedError,
} from '../../../../services/ImageMigrationService';
import type { LayoutSection } from '@segmently/schema-models/v2';
import { generateSectionId } from '../../../../config/flexibleSectionDefaults';

// ============================================================================
// IMAGE THUMBNAIL COMPONENT
// ============================================================================

interface ImageThumbnailProps {
  url: string;
  size?: number;
}

/**
 * Small image thumbnail with error fallback
 */
const ImageThumbnail: React.FC<ImageThumbnailProps> = ({ url, size = 32 }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          borderRadius: 0.5,
          flexShrink: 0,
        }}
      >
        <BrokenImageIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={url}
      alt=""
      onError={() => setHasError(true)}
      sx={{
        width: size,
        height: size,
        objectFit: 'cover',
        borderRadius: 0.5,
        flexShrink: 0,
        bgcolor: 'action.hover',
      }}
    />
  );
};

// ============================================================================
// TYPES
// ============================================================================

export interface ImageUrlScannerDialogProps {
  /** Whether dialog is open */
  open: boolean;
  /** Callback when dialog closes */
  onClose: () => void;
  /** HTML content to scan */
  htmlContent: string;
  /** Upload context for CDN paths */
  uploadContext: OnboardingUploadContext;
  /** Callback when user applies replacements */
  onApplyReplacements: (newHtml: string, mappings: UrlMapping[]) => void;
  /** V2 mode: use OnboardingMediaUploadService for multi-size upload (origin + small) */
  useMultiSizeUpload?: boolean;
  /** Existing child sections for duplicate name detection */
  existingChildSections?: LayoutSection[];
  /** Callback to create Media DataSource entries from uploaded images */
  onCreateDataSources?: (sections: LayoutSection[]) => void;
  /** Current editing locale (needed for DataSource asset translations) */
  locale?: string;
}

/** Dialog step */
type Step = 'migrate' | 'preview';

// ============================================================================
// COMPONENT
// ============================================================================

export const ImageUrlScannerDialog: React.FC<ImageUrlScannerDialogProps> = ({
  open,
  onClose,
  htmlContent,
  uploadContext,
  onApplyReplacements,
  useMultiSizeUpload = false,
  existingChildSections,
  onCreateDataSources,
  locale,
}) => {
  // ============================================================================
  // STATE
  // ============================================================================

  // Step management
  const [step, setStep] = useState<Step>('migrate');
  const [updatedHtml, setUpdatedHtml] = useState<string>('');

  // Migration state
  const [mappings, setMappings] = useState<UrlMapping[]>([]);
  const [selectedUrls, setSelectedUrls] = useState<Set<string>>(new Set());
  const [isMigrating, setIsMigrating] = useState(false);
  const [currentMigrating, setCurrentMigrating] = useState<string | null>(null);

  // Ref for hidden file input (single file - manual fallback)
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [manualUploadUrl, setManualUploadUrl] = useState<string | null>(null);

  // Ref for hidden folder input (webkitdirectory - local assets)
  const folderInputRef = useRef<HTMLInputElement>(null);

  // Duplicate detection state
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateItems, setDuplicateItems] = useState<Array<{ name: string; mapping: UrlMapping }>>([]);
  const [duplicateResolutions, setDuplicateResolutions] = useState<Record<string, 'overwrite' | 'keep_both'>>({});
  const pendingApplyRef = useRef<{ newHtml: string; mappings: UrlMapping[] } | null>(null);

  // Migration service
  const migrationService = useMemo(() => new ImageMigrationService(), []);

  // ============================================================================
  // SCAN ON OPEN
  // ============================================================================

  useEffect(() => {
    if (open && htmlContent) {
      // Scan HTML for all image URLs (HTTP + local paths)
      const scannedUrls = scanHtmlForImageUrls(htmlContent);
      const externalUrls = filterExternalUrls(scannedUrls);

      // Initialize mappings - local paths get 'local' status, HTTP get 'pending'
      const initialMappings: UrlMapping[] = externalUrls.map((item) => ({
        originalUrl: item.url,
        newCdnUrl: '',
        status: item.isLocalPath ? 'local' : 'pending',
        isLocalPath: item.isLocalPath,
      }));

      setMappings(initialMappings);
      // Only auto-select HTTP URLs for migration, not local paths
      setSelectedUrls(new Set(externalUrls.filter((item) => !item.isLocalPath).map((item) => item.url)));
    }
  }, [open, htmlContent]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setMappings([]);
      setSelectedUrls(new Set());
      setIsMigrating(false);
      setCurrentMigrating(null);
      setManualUploadUrl(null);
      setStep('migrate');
      setUpdatedHtml('');
      setDuplicateDialogOpen(false);
      setDuplicateItems([]);
      setDuplicateResolutions({});
      pendingApplyRef.current = null;
      // Reset file inputs
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (folderInputRef.current) folderInputRef.current.value = '';
    }
  }, [open]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const successCount = mappings.filter(
    (m) => m.status === 'success' || m.status === 'manual_upload'
  ).length;

  const failedCount = mappings.filter(
    (m) => m.status === 'failed' || m.status === 'cors_blocked'
  ).length;

  const successfulMappings = mappings.filter(
    (m) => (m.status === 'success' || m.status === 'manual_upload') && m.newCdnUrl
  );

  const canGenerateScript = successCount > 0;

  const localMappings = mappings.filter((m) => m.isLocalPath);
  const localPendingCount = localMappings.filter((m) => m.status === 'local').length;
  const hasLocalPaths = localMappings.length > 0;

  // Hint text showing which folder to select (common prefix of local paths)
  const localFolderHint = useMemo(() => {
    const paths = localMappings.map((m) => m.originalUrl);
    if (paths.length === 0) return '';
    // Find common prefix directory
    const dirs = paths.map((p) => {
      const parts = p.replace(/^\.\//, '').split('/');
      return parts.length > 1 ? parts[0] : '';
    });
    const uniqueDirs = [...new Set(dirs)].filter(Boolean);
    if (uniqueDirs.length === 1) return uniqueDirs[0] + '/';
    return '';
  }, [localMappings]);

  // ============================================================================
  // HANDLERS - MIGRATION STEP
  // ============================================================================

  /**
   * Toggle URL selection
   */
  const handleToggleUrl = useCallback((url: string) => {
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      if (next.has(url)) {
        next.delete(url);
      } else {
        next.add(url);
      }
      return next;
    });
  }, []);

  /**
   * Select/Deselect all
   */
  const handleSelectAll = useCallback((selectAll: boolean) => {
    if (selectAll) {
      setSelectedUrls(new Set(mappings.map((m) => m.originalUrl)));
    } else {
      setSelectedUrls(new Set());
    }
  }, [mappings]);

  /**
   * Migrate selected URLs
   */
  const handleMigrate = useCallback(async () => {
    if (selectedUrls.size === 0) return;

    setIsMigrating(true);

    for (const url of Array.from(selectedUrls)) {
      // Skip already successful
      const mapping = mappings.find((m) => m.originalUrl === url);
      if (mapping?.status === 'success' || mapping?.status === 'manual_upload') {
        continue;
      }

      setCurrentMigrating(url);

      // Update status to uploading
      setMappings((prev) =>
        prev.map((m) =>
          m.originalUrl === url ? { ...m, status: 'uploading' as const, error: undefined } : m
        )
      );

      try {
        const result = useMultiSizeUpload
          ? await migrationService.migrateFromUrlWithResize(url, uploadContext)
          : await migrationService.migrateFromUrl(url, uploadContext);

        // Update status to success with optional multi-size data
        setMappings((prev) =>
          prev.map((m) =>
            m.originalUrl === url
              ? {
                  ...m,
                  status: 'success' as const,
                  newCdnUrl: result.cdnUrl,
                  assetUrl: result.assetUrl,
                  filename: result.filename,
                }
              : m
          )
        );
      } catch (error) {
        const isCors = error instanceof CorsBlockedError;
        setMappings((prev) =>
          prev.map((m) =>
            m.originalUrl === url
              ? {
                  ...m,
                  status: isCors ? ('cors_blocked' as const) : ('failed' as const),
                  error: error instanceof Error ? error.message : 'Unknown error',
                }
              : m
          )
        );
      }
    }

    setIsMigrating(false);
    setCurrentMigrating(null);
  }, [selectedUrls, mappings, migrationService, uploadContext, useMultiSizeUpload]);

  /**
   * Open file input for manual upload
   */
  const handleOpenManualUpload = useCallback((url: string) => {
    setManualUploadUrl(url);
    fileInputRef.current?.click();
  }, []);

  /**
   * Handle manual file upload
   */
  const handleManualFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !manualUploadUrl) return;

      // Reset input
      event.target.value = '';

      const url = manualUploadUrl;
      setManualUploadUrl(null);

      // Update status to uploading
      setMappings((prev) =>
        prev.map((m) =>
          m.originalUrl === url ? { ...m, status: 'uploading' as const, error: undefined } : m
        )
      );

      try {
        const result = useMultiSizeUpload
          ? await migrationService.uploadManuallyWithResize(file, url, uploadContext)
          : await migrationService.uploadManually(file, url, uploadContext);

        // Update status to manual_upload (success via manual)
        setMappings((prev) =>
          prev.map((m) =>
            m.originalUrl === url
              ? {
                  ...m,
                  status: 'manual_upload' as const,
                  newCdnUrl: result.cdnUrl,
                  assetUrl: result.assetUrl,
                  filename: result.filename,
                }
              : m
          )
        );
      } catch (error) {
        setMappings((prev) =>
          prev.map((m) =>
            m.originalUrl === url
              ? {
                  ...m,
                  status: 'failed' as const,
                  error: error instanceof Error ? error.message : 'Upload failed',
                }
              : m
          )
        );
      }
    },
    [manualUploadUrl, migrationService, uploadContext, useMultiSizeUpload]
  );

  /**
   * Copy CDN URL to clipboard
   */
  const handleCopyUrl = useCallback((url: string) => {
    navigator.clipboard.writeText(url);
  }, []);

  /**
   * Open folder picker for local assets
   */
  const handleOpenFolderPicker = useCallback(() => {
    folderInputRef.current?.click();
  }, []);

  /**
   * Handle folder selection - match files to local paths and upload automatically
   */
  const handleFolderSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      // Reset input for re-selection
      const fileList = Array.from(files);
      event.target.value = '';

      // Get local paths that need matching
      const localPaths = mappings
        .filter((m) => m.isLocalPath && m.status === 'local')
        .map((m) => m.originalUrl);

      if (localPaths.length === 0) return;

      // Match files to paths
      const { matched, unmatched } = matchLocalFilesToPaths(localPaths, fileList);

      // Mark unmatched as failed
      if (unmatched.length > 0) {
        setMappings((prev) =>
          prev.map((m) =>
            unmatched.includes(m.originalUrl)
              ? { ...m, status: 'failed' as const, error: 'File not found in selected folder' }
              : m
          )
        );
      }

      // Upload matched files sequentially
      setIsMigrating(true);
      for (const match of matched) {
        setCurrentMigrating(match.localPath);

        // Update status to uploading
        setMappings((prev) =>
          prev.map((m) =>
            m.originalUrl === match.localPath
              ? { ...m, status: 'uploading' as const, error: undefined }
              : m
          )
        );

        try {
          const result = useMultiSizeUpload
            ? await migrationService.uploadManuallyWithResize(match.file, match.localPath, uploadContext)
            : await migrationService.uploadManually(match.file, match.localPath, uploadContext);

          setMappings((prev) =>
            prev.map((m) =>
              m.originalUrl === match.localPath
                ? {
                    ...m,
                    status: 'success' as const,
                    newCdnUrl: result.cdnUrl,
                    assetUrl: result.assetUrl,
                    filename: result.filename,
                  }
                : m
            )
          );
        } catch (error) {
          setMappings((prev) =>
            prev.map((m) =>
              m.originalUrl === match.localPath
                ? {
                    ...m,
                    status: 'failed' as const,
                    error: error instanceof Error ? error.message : 'Upload failed',
                  }
                : m
            )
          );
        }
      }

      setIsMigrating(false);
      setCurrentMigrating(null);
    },
    [mappings, migrationService, uploadContext, useMultiSizeUpload]
  );

  // ============================================================================
  // HANDLERS - PREVIEW STEP
  // ============================================================================

  /**
   * Generate updated script and go to preview
   */
  const handleGenerateScript = useCallback(() => {
    const newHtml = replaceUrlsInHtml(htmlContent, mappings);
    setUpdatedHtml(newHtml);
    setStep('preview');
  }, [htmlContent, mappings]);

  /**
   * Go back to migrate step
   */
  const handleBack = useCallback(() => {
    setStep('migrate');
    setUpdatedHtml('');
  }, []);

  /**
   * Download mapping JSON
   */
  const handleDownloadMapping = useCallback(() => {
    downloadMappingJson(mappings);
  }, [mappings]);

  /**
   * Download updated script
   */
  const handleDownloadScript = useCallback(() => {
    downloadUpdatedScript(updatedHtml);
  }, [updatedHtml]);

  /**
   * Extract filename without extension from a URL or filename string.
   */
  const extractLabel = useCallback((urlOrFilename: string): string => {
    // Try to extract filename from URL path
    let name = urlOrFilename;
    try {
      const parsed = new URL(urlOrFilename);
      name = parsed.pathname.split('/').pop() || urlOrFilename;
    } catch {
      // Not a URL — use as-is, take last path segment
      name = urlOrFilename.split('/').pop() || urlOrFilename;
    }
    // Remove extension
    const dotIdx = name.lastIndexOf('.');
    if (dotIdx > 0) name = name.substring(0, dotIdx);
    // Sanitize: replace non-alphanumeric with underscore
    return name.replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_').toLowerCase();
  }, []);

  /**
   * Build Media LayoutSections from successful mappings.
   * Handles duplicate name resolution based on user choices.
   */
  const buildMediaDataSources = useCallback(
    (currentMappings: UrlMapping[], resolutions: Record<string, 'overwrite' | 'keep_both'>): LayoutSection[] => {
      const effectiveLocale = locale || uploadContext.locale || 'en';
      const existingLabels = (existingChildSections ?? [])
        .filter(s => (s.kind as string) === 'Media')
        .map(s => (s.label ?? '').toLowerCase());

      const successMappings = currentMappings.filter(
        m => (m.status === 'success' || m.status === 'manual_upload') && m.assetUrl
      );

      const newSections: LayoutSection[] = [];
      const usedLabels = new Set(existingLabels);
      let order = (existingChildSections ?? []).length;

      for (const mapping of successMappings) {
        let label = extractLabel(mapping.filename || mapping.originalUrl);
        const isDuplicate = usedLabels.has(label.toLowerCase());

        if (isDuplicate) {
          const resolution = resolutions[label];
          if (resolution === 'overwrite') {
            // Skip — the parent will handle removing the old one
            // Still add the new section with the same label
          } else {
            // keep_both — add numeric suffix
            let counter = 2;
            while (usedLabels.has(`${label}_${counter}`.toLowerCase())) counter++;
            label = `${label}_${counter}`;
          }
        }

        usedLabels.add(label.toLowerCase());

        newSections.push({
          id: generateSectionId(),
          kind: 'Media',
          label,
          order: order++,
          layout: { heightKind: 'auto', flexGrow: 1, flexShrink: 1, scrollable: false },
          mediaContent: {
            kind: 'Image',
            content: {
              image: {
                translations: {
                  [effectiveLocale]: {
                    original: mapping.assetUrl!.origin,
                    small: mapping.assetUrl!.small,
                  },
                },
              },
            },
            appearance: {},
          },
        } as unknown as LayoutSection);
      }

      return newSections;
    },
    [locale, uploadContext.locale, existingChildSections, extractLabel],
  );

  /**
   * Find duplicate names between new uploads and existing child sections.
   */
  const findDuplicates = useCallback(
    (currentMappings: UrlMapping[]): Array<{ name: string; mapping: UrlMapping }> => {
      const existingLabels = new Set(
        (existingChildSections ?? [])
          .filter(s => (s.kind as string) === 'Media')
          .map(s => (s.label ?? '').toLowerCase())
      );

      const successMappings = currentMappings.filter(
        m => (m.status === 'success' || m.status === 'manual_upload') && m.assetUrl
      );

      const dupes: Array<{ name: string; mapping: UrlMapping }> = [];
      for (const mapping of successMappings) {
        const label = extractLabel(mapping.filename || mapping.originalUrl);
        if (existingLabels.has(label.toLowerCase())) {
          dupes.push({ name: label, mapping });
        }
      }
      return dupes;
    },
    [existingChildSections, extractLabel],
  );

  /**
   * Apply changes to editor + create DataSources
   */
  const handleApply = useCallback(() => {
    // If V2 mode with DataSources, check for duplicates first
    if (useMultiSizeUpload && onCreateDataSources) {
      const dupes = findDuplicates(mappings);

      if (dupes.length > 0) {
        // Show duplicate resolution dialog
        pendingApplyRef.current = { newHtml: updatedHtml, mappings };
        setDuplicateItems(dupes);
        // Default all to keep_both
        const defaultResolutions: Record<string, 'overwrite' | 'keep_both'> = {};
        for (const d of dupes) defaultResolutions[d.name] = 'keep_both';
        setDuplicateResolutions(defaultResolutions);
        setDuplicateDialogOpen(true);
        return;
      }

      // No duplicates — create DataSources directly
      const newSections = buildMediaDataSources(mappings, {});
      if (newSections.length > 0) onCreateDataSources(newSections);
    }

    onApplyReplacements(updatedHtml, mappings);
    onClose();
  }, [updatedHtml, mappings, onApplyReplacements, onClose, useMultiSizeUpload, onCreateDataSources, findDuplicates, buildMediaDataSources]);

  /**
   * Confirm duplicate resolution and apply
   */
  const handleDuplicateConfirm = useCallback(() => {
    if (!pendingApplyRef.current || !onCreateDataSources) return;

    const { newHtml, mappings: pendingMappings } = pendingApplyRef.current;

    // Build sections with user-chosen resolutions
    const newSections = buildMediaDataSources(pendingMappings, duplicateResolutions);

    // For 'overwrite' resolutions, filter out the old sections with matching labels
    const overwriteLabels = new Set(
      Object.entries(duplicateResolutions)
        .filter(([, v]) => v === 'overwrite')
        .map(([k]) => k.toLowerCase())
    );

    if (overwriteLabels.size > 0) {
      // Pass overwrite info via a special combined callback
      onCreateDataSources(newSections.map(s => ({
        ...s,
        // Mark sections that should replace existing ones
        _replaceLabel: overwriteLabels.has((s.label ?? '').toLowerCase()) ? s.label : undefined,
      } as unknown as LayoutSection)));
    } else {
      onCreateDataSources(newSections);
    }

    onApplyReplacements(newHtml, pendingMappings);
    setDuplicateDialogOpen(false);
    pendingApplyRef.current = null;
    onClose();
  }, [onCreateDataSources, onApplyReplacements, onClose, buildMediaDataSources, duplicateResolutions]);

  // ============================================================================
  // RENDER - MIGRATE STEP
  // ============================================================================

  const renderMigrateStep = () => (
    <>
      {/* Summary */}
      <Box sx={{ mb: 2 }}>
        {mappings.length === 0 ? (
          <Alert severity="info">All images are already on Segmently CDN</Alert>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Found {mappings.length} image{mappings.length !== 1 ? 's' : ''}
            {successCount > 0 && (
              <Chip
                label={`${successCount} migrated`}
                size="small"
                color="success"
                sx={{ ml: 1 }}
              />
            )}
            {localPendingCount > 0 && (
              <Chip
                label={`${localPendingCount} local`}
                size="small"
                color="info"
                sx={{ ml: 1 }}
              />
            )}
            {failedCount > 0 && (
              <Chip
                label={`${failedCount} need manual upload`}
                size="small"
                color="warning"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
        )}
      </Box>

      {/* Local assets folder picker */}
      {hasLocalPaths && localPendingCount > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<FolderOpenIcon />}
              onClick={handleOpenFolderPicker}
              disabled={isMigrating}
              data-testid="image-scanner-select-folder"
            >
              Select Folder
            </Button>
          }
        >
          {localPendingCount} local image{localPendingCount !== 1 ? 's' : ''} found
          {localFolderHint && (
            <>
              {' '}— select folder containing <strong>{localFolderHint}</strong>
            </>
          )}
        </Alert>
      )}

      {/* Progress bar during migration */}
      {isMigrating && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Migrating: {currentMigrating ? truncateUrl(currentMigrating, 50) : '...'}
          </Typography>
        </Box>
      )}

      {/* URL List */}
      {mappings.length > 0 && (
        <Box
          sx={{
            maxHeight: 300,
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            mb: 2,
          }}
        >
          {/* Header row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            <Checkbox
              size="small"
              checked={selectedUrls.size === mappings.length}
              indeterminate={selectedUrls.size > 0 && selectedUrls.size < mappings.length}
              onChange={(e) => handleSelectAll(e.target.checked)}
              disabled={isMigrating}
              data-testid="image-scanner-select-all"
            />
            <Typography variant="caption" fontWeight={500} sx={{ flex: 1 }}>
              URL
            </Typography>
            <Typography variant="caption" fontWeight={500} sx={{ width: 100, textAlign: 'center' }}>
              Status
            </Typography>
            <Typography variant="caption" fontWeight={500} sx={{ width: 80, textAlign: 'center' }}>
              Action
            </Typography>
          </Box>

          {/* URL rows */}
          {mappings.map((mapping) => (
            <Box
              key={mapping.originalUrl}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': { borderBottom: 'none' },
                bgcolor:
                  mapping.status === 'success' || mapping.status === 'manual_upload'
                    ? 'success.50'
                    : mapping.status === 'cors_blocked' || mapping.status === 'failed'
                    ? 'warning.50'
                    : 'transparent',
              }}
            >
              <Checkbox
                size="small"
                checked={selectedUrls.has(mapping.originalUrl)}
                onChange={() => handleToggleUrl(mapping.originalUrl)}
                disabled={isMigrating}
              />

              {/* Image thumbnail - show CDN URL if migrated, otherwise original */}
              <Box sx={{ mr: 1 }}>
                <ImageThumbnail
                  url={mapping.newCdnUrl || mapping.originalUrl}
                  size={32}
                />
              </Box>

              {/* URL with tooltip */}
              <Tooltip title={mapping.originalUrl} placement="top">
                <Typography
                  variant="caption"
                  sx={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontFamily: 'monospace',
                  }}
                >
                  {truncateUrl(mapping.originalUrl, 40)}
                </Typography>
              </Tooltip>

              {/* Status */}
              <Box sx={{ width: 100, textAlign: 'center' }}>
                {renderStatus(mapping.status)}
              </Box>

              {/* Action */}
              <Box sx={{ width: 80, textAlign: 'center' }}>
                {(mapping.status === 'success' || mapping.status === 'manual_upload') &&
                  mapping.newCdnUrl && (
                    <Tooltip title="Copy CDN URL">
                      <IconButton size="small" onClick={() => handleCopyUrl(mapping.newCdnUrl)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                {(mapping.status === 'cors_blocked' || mapping.status === 'failed' || mapping.status === 'local') && (
                  <Tooltip title={mapping.status === 'local' ? 'Upload file manually' : 'Upload manually'}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenManualUpload(mapping.originalUrl)}
                      disabled={isMigrating}
                    >
                      <CloudUploadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {mapping.status === 'pending' && (
                  <Typography variant="caption" color="text.disabled">
                    -
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Action buttons */}
      {mappings.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleMigrate}
            disabled={isMigrating || selectedUrls.size === 0}
            data-testid="image-scanner-migrate"
          >
            Migrate Selected ({selectedUrls.size})
          </Button>

          {/* Show "Generate Updated Script" button only after successful migrations */}
          {canGenerateScript && (
            <Button
              variant="outlined"
              size="small"
              color="success"
              endIcon={<ArrowForwardIcon />}
              onClick={handleGenerateScript}
              disabled={isMigrating}
              data-testid="image-scanner-next"
            >
              Generate Updated Script
            </Button>
          )}
        </Box>
      )}
    </>
  );

  // ============================================================================
  // RENDER - PREVIEW STEP
  // ============================================================================

  const renderPreviewStep = () => (
    <>
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          data-testid="image-scanner-back"
        >
          Back
        </Button>
        <Typography variant="body2" color="text.secondary">
          {successfulMappings.length} URL{successfulMappings.length !== 1 ? 's' : ''} will be replaced
        </Typography>
      </Box>

      {/* Simple Diff View */}
      <Box
        sx={{
          maxHeight: 350,
          overflowY: 'auto',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          mb: 2,
          bgcolor: 'background.paper',
        }}
      >
        {successfulMappings.map((mapping, index) => (
          <Box key={mapping.originalUrl}>
            {/* Old URL (red/strikethrough) */}
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'error.50',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: 'error.main', fontWeight: 500, flexShrink: 0 }}
              >
                ❌
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  color: 'error.main',
                  textDecoration: 'line-through',
                  wordBreak: 'break-all',
                }}
              >
                {mapping.originalUrl}
              </Typography>
            </Box>

            {/* New URL (green) */}
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'success.50',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: 'success.main', fontWeight: 500, flexShrink: 0 }}
              >
                ✅
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  color: 'success.main',
                  wordBreak: 'break-all',
                }}
              >
                {mapping.newCdnUrl}
              </Typography>
            </Box>

            {/* Divider between pairs */}
            {index < successfulMappings.length - 1 && <Divider />}
          </Box>
        ))}
      </Box>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadMapping}
        >
          Download Mapping
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadScript}
        >
          Download Script
        </Button>
      </Box>
    </>
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <SDialog
      open={open}
      onClose={onClose}
      title={step === 'migrate' ? 'Scan External Images' : 'Preview Changes'}
      maxWidth="md"
      data-testid="image-scanner-dialog"
      primaryAction={
        step === 'preview'
          ? {
              label: 'Apply to Code',
              onClick: handleApply,
              'data-testid': 'image-scanner-apply',
            }
          : undefined
      }
    >
      {/* Hidden file input for manual upload (single file) */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleManualFileSelect}
      />

      {/* Hidden folder input for local assets (webkitdirectory) */}
      {/* eslint-disable-next-line react/no-unknown-property */}
      <input
        ref={folderInputRef}
        type="file"
        // @ts-expect-error webkitdirectory is a non-standard but widely supported attribute
        webkitdirectory=""
        directory=""
        multiple
        style={{ display: 'none' }}
        onChange={handleFolderSelect}
        data-testid="image-scanner-folder-input"
      />

      {step === 'migrate' ? renderMigrateStep() : renderPreviewStep()}

      {/* Duplicate Name Resolution Dialog */}
      <Dialog open={duplicateDialogOpen} onClose={() => setDuplicateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Duplicate DataSource Names</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Some uploaded images have the same name as existing data sources. Choose how to handle each:
          </Typography>
          <List dense>
            {duplicateItems.map(({ name }) => (
              <ListItem key={name} sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  &quot;{name}&quot;
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    onClick={() => setDuplicateResolutions(prev => ({ ...prev, [name]: 'overwrite' }))}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Radio
                        size="small"
                        checked={duplicateResolutions[name] === 'overwrite'}
                        onChange={() => setDuplicateResolutions(prev => ({ ...prev, [name]: 'overwrite' }))}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Overwrite existing" primaryTypographyProps={{ variant: 'body2' }} />
                  </Box>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    onClick={() => setDuplicateResolutions(prev => ({ ...prev, [name]: 'keep_both' }))}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Radio
                        size="small"
                        checked={duplicateResolutions[name] === 'keep_both'}
                        onChange={() => setDuplicateResolutions(prev => ({ ...prev, [name]: 'keep_both' }))}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Keep both (add suffix)" primaryTypographyProps={{ variant: 'body2' }} />
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleDuplicateConfirm}>Apply</Button>
        </DialogActions>
      </Dialog>
    </SDialog>
  );
};

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Truncate URL for display
 */
function truncateUrl(url: string, maxLength: number): string {
  if (url.length <= maxLength) return url;
  return url.substring(0, maxLength - 3) + '...';
}

/**
 * Render status chip/icon
 */
function renderStatus(status: UrlMapping['status']): React.ReactNode {
  switch (status) {
    case 'success':
    case 'manual_upload':
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Done"
          size="small"
          color="success"
          variant="outlined"
        />
      );
    case 'cors_blocked':
      return (
        <Chip
          icon={<WarningIcon />}
          label="CORS"
          size="small"
          color="warning"
          variant="outlined"
        />
      );
    case 'failed':
      return (
        <Chip icon={<ErrorIcon />} label="Failed" size="small" color="error" variant="outlined" />
      );
    case 'uploading':
      return (
        <Chip label="Uploading..." size="small" color="primary" variant="outlined" />
      );
    case 'local':
      return (
        <Chip
          icon={<FolderOpenIcon />}
          label="Local"
          size="small"
          color="info"
          variant="outlined"
        />
      );
    case 'pending':
    default:
      return (
        <Chip label="Pending" size="small" variant="outlined" />
      );
  }
}

export default ImageUrlScannerDialog;
